<template>
  <div id="app">
    <MainNavigation v-if="!isV2" />
    <main :class="isV2 ? 'app-content-v2' : 'app-content'">
      <router-view />
    </main>
    <Analytics />
    
    <!-- Global Admin Authentication Modal -->
    <AdminAuthModal
      :visible="showAuthModal"
      @authenticated="onAuthenticated"
      @cancelled="onAuthCancelled"
      @close="closeAuthModal"
    />
  </div>
</template>

<script>
import MainNavigation from './components/MainNavigation.vue';
import AdminAuthModal from './components/modals/AdminAuthModal.vue';
import { Analytics } from '@vercel/analytics/vue';
import { useAdminAuth } from './composables/useAdminAuth';

export default {
  name: 'App',
  components: {
    MainNavigation,
    AdminAuthModal,
    Analytics
  },
  computed: {
    isV2() {
      // Optional chaining: some existing tests mount App without a router.
      return Boolean(this.$route?.matched?.some((r) => r.meta && r.meta.v2));
    },
  },
  setup() {
    const {
      showAuthModal,
      onAuthenticated,
      onAuthCancelled,
      closeAuthModal
    } = useAdminAuth();

    return {
      showAuthModal,
      onAuthenticated,
      onAuthCancelled,
      closeAuthModal
    };
  }
}
</script>

<style>
/* Import Enhanced Tier Color System */
@import './styles/tier-colors.css';

/* Modern CSS Reset and Global Styles */
* {
  box-sizing: border-box;
}

body {
  margin: 0;
  padding: 0;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0f0f0f 100%);
  background-attachment: fixed;
  color: #f3f3f3;
  line-height: 1.6;
  font-weight: 400;
  overflow-x: hidden;
}

#app {
  font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: left;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.app-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* Modern Button Base Styles */
button {
  font-family: inherit;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
  transition: left 0.5s;
}

button:hover::before {
  left: 100%;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

button:disabled::before {
  display: none;
}

/* Modern Input Styles */
input, select, textarea {
  font-family: inherit;
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  color: #f3f3f3;
  font-size: 14px;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
}

input:focus, select:focus, textarea:focus {
  outline: none;
  border-color: #2d8cf0;
  box-shadow: 0 0 0 3px rgba(45, 140, 240, 0.1);
  background: rgba(255, 255, 255, 0.08);
}

input::placeholder {
  color: rgba(243, 243, 243, 0.5);
}

/* Modern Link Styles */
a {
  color: #60a5fa;
  text-decoration: none;
  transition: all 0.2s ease;
}

a:hover {
  color: #93c5fd;
  text-decoration: underline;
}

/* Scrollbar Styles */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Modern Card Styles */
.card {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
  border-color: rgba(255, 255, 255, 0.15);
}

/* Animation Classes */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active, .slide-up-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-up-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.slide-up-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}
</style>
